
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart2, TrendingUp, Users, DollarSign } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

// Sample data for the charts
const performanceData = [
  { date: '2024-01', value: 100000 },
  { date: '2024-02', value: 115000 },
  { date: '2024-03', value: 124892 },
  { date: '2024-04', value: 128000 },
];

const strategyData = [
  { name: 'Momentum', performance: 15.2 },
  { name: 'Mean Rev', performance: 8.4 },
  { name: 'ML Strategy', performance: 12.7 },
  { name: 'Trend', performance: -2.3 },
  { name: 'Macro', performance: 5.8 },
];

const Analytics = () => {
  return (
    <div className="container mx-auto p-4 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-300">Track your portfolio performance and insights</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-300">Portfolio Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" />
              <span className="text-2xl font-bold text-gray-900 dark:text-white">$124,892</span>
            </div>
            <span className="text-xs font-medium text-green-600 dark:text-green-400">+2.5% from last month</span>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-300">Active Strategies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
              <span className="text-2xl font-bold text-gray-900 dark:text-white">8</span>
            </div>
            <span className="text-xs font-medium text-blue-600 dark:text-blue-400">5 performing above benchmark</span>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-300">Trading Volume</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <BarChart2 className="h-4 w-4 text-purple-600 dark:text-purple-400 mr-2" />
              <span className="text-2xl font-bold text-gray-900 dark:text-white">$45,231</span>
            </div>
            <span className="text-xs font-medium text-purple-600 dark:text-purple-400">Last 24 hours</span>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-300">Risk Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <Users className="h-4 w-4 text-orange-600 dark:text-orange-400 mr-2" />
              <span className="text-2xl font-bold text-gray-900 dark:text-white">Medium</span>
            </div>
            <span className="text-xs font-medium text-orange-600 dark:text-orange-400">Within target range</span>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white">Portfolio Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={performanceData}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" strokeOpacity={0.5} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#6b7280', fontWeight: 500 }}
                    tickLine={{ stroke: '#9ca3af' }}
                    axisLine={{ stroke: '#9ca3af' }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return date.toLocaleDateString('default', { month: 'short' });
                    }}
                  />
                  <YAxis 
                    tick={{ fill: '#6b7280', fontWeight: 500 }}
                    tickLine={{ stroke: '#9ca3af' }}
                    axisLine={{ stroke: '#9ca3af' }}
                    tickFormatter={(value) => `$${value.toLocaleString()}`}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                    labelStyle={{ color: '#e5e7eb', fontWeight: 'bold' }}
                    itemStyle={{ color: '#f3f4f6' }}
                    formatter={(value: number) => [`$${value.toLocaleString()}`, 'Portfolio Value']}
                    labelFormatter={(label) => {
                      const date = new Date(label);
                      return date.toLocaleDateString('default', { month: 'long', year: 'numeric' });
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#8b5cf6" 
                    strokeWidth={3}
                    fillOpacity={1} 
                    fill="url(#colorValue)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white">Strategy Analytics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={strategyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" strokeOpacity={0.5} />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fill: '#6b7280', fontWeight: 500 }}
                    tickLine={{ stroke: '#9ca3af' }}
                    axisLine={{ stroke: '#9ca3af' }}
                  />
                  <YAxis 
                    tick={{ fill: '#6b7280', fontWeight: 500 }}
                    tickLine={{ stroke: '#9ca3af' }}
                    axisLine={{ stroke: '#9ca3af' }}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                    labelStyle={{ color: '#e5e7eb', fontWeight: 'bold' }}
                    itemStyle={{ color: '#f3f4f6' }}
                    formatter={(value: number) => [`${value.toFixed(1)}%`, 'Performance']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="performance" 
                    stroke="#10b981"
                    strokeWidth={3}
                    dot={{ fill: '#10b981', stroke: '#10b981', strokeWidth: 2, r: 4 }}
                    activeDot={{ fill: '#10b981', stroke: '#fff', strokeWidth: 2, r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
